import * as React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import OutlinedInput from '@mui/material/OutlinedInput';
import { useAuthManager } from "../../hooks/useAuthHook";

interface ForgotPasswordProps {
  open: boolean;
  handleClose: () => void;
}

export default function ForgotPassword({ open, handleClose }: ForgotPasswordProps) {
  const { useForgotPassword } = useAuthManager();
  const { mutate: forgotPassword } = useForgotPassword();

  // State to manage the confirmation message
  const [emailSent, setEmailSent] = React.useState(false);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const email = formData.get('email') as string;

    forgotPassword(
      { email },
      {
        onSuccess: () => {
          setEmailSent(true); // Show the confirmation message
        },
        onError: (error: any) => {
          console.error("Failed to send password reset email", error);
        },
      }
    );
  };

  const handleDialogClose = () => {
    setEmailSent(false); // Reset the state on dialog close
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit,
        sx: { backgroundImage: 'none' },
      }}
    >
      {!emailSent ? (
        <>
          <DialogTitle>Reset password</DialogTitle>
          <DialogContent
            sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}
          >
            <DialogContentText>
              Enter your account&apos;s email address, and we&apos;ll send you a link to
              reset your password.
            </DialogContentText>
            <OutlinedInput
              autoFocus
              required
              margin="dense"
              id="email"
              name="email"
              placeholder="Email address"
              type="email"
              fullWidth
            />
          </DialogContent>
          <DialogActions sx={{ pb: 3, px: 3 }}>
            <Button onClick={handleDialogClose}>Cancel</Button>
            <Button variant="contained" type="submit">
              Continue
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogTitle>Email Sent</DialogTitle>
          <DialogContent>
            <DialogContentText>
              A password reset link has been sent to your email. Please check your inbox, or spam/junk folder, and follow the instructions.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ pb: 3, px: 3 }}>
            <Button onClick={handleDialogClose} variant="contained">
              Close
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
